import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Toaster } from '../components/ui/toaster';
import { Sprout, Upload, Trash2, Download, FileText, LogOut, ExternalLink, Shield } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import EventAdmin from '../components/EventAdmin';
import { adminLogin, deleteStudy, fetchStudies, setAdminKey, clearAdminKey, getAdminKey, uploadStudy, studyDownloadUrl, fetchNextSteps } from '../lib/content';

function LoginView({ onLogin }) {
  const { toast } = useToast();
  const [val, setVal] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!val.trim()) return;
    setLoading(true);
    try {
      setAdminKey(val.trim());
      await adminLogin(val.trim());
      onLogin();
    } catch (err) {
      clearAdminKey();
      toast({ title: 'Incorrect key', description: 'That admin key did not work. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#f5efe3] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-[#e5dcc5] rounded-3xl p-8 shadow-[0_30px_80px_-30px_rgba(31,58,42,0.3)]">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-12 rounded-full bg-[#1f3a2a] flex items-center justify-center text-[#d4a63d]"><Shield size={22} /></span>
          <div>
            <p className="text-[11px] uppercase tracking-wider-xtra text-[#b88a2a] font-medium">Pastor Dashboard</p>
            <h1 className="font-serif-display text-2xl text-[#1f3a2a]">Planted Ministries</h1>
          </div>
        </div>
        <p className="text-sm text-[#4a5a4e] mb-6">Enter your admin key to manage Bible studies and sermons.</p>
        <Label className="text-[#1f3a2a]">Admin key</Label>
        <Input
          type="password"
          data-testid="admin-login-key-input"
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Paste your admin key"
          className="mt-2 bg-[#faf6ea] border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]"
        />
        <Button type="submit" disabled={loading} data-testid="admin-login-submit-btn" className="mt-6 w-full bg-[#1f3a2a] hover:bg-[#2e5340] text-[#f5efe3] rounded-full h-12 font-medium">
          {loading ? 'Checking…' : 'Sign in'}
        </Button>
        <a href="/" className="mt-5 block text-center text-xs text-[#8a7a4e] hover:text-[#1f3a2a]">← Back to the site</a>
      </form>
    </div>
  );
}

function fmtSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function NextStepsAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { fetchNextSteps().then((d) => setItems(d.items || [])).catch((err) => console.error('Failed to load next steps', err)); }, []);
  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-16" data-testid="admin-next-steps">
      <h2 className="font-serif-display text-2xl text-[#1f3a2a]">Next step requests <span className="text-[#8a7a4e] text-base">({items.length})</span></h2>
      <p className="text-sm text-[#4a5a4e] mt-1">People who asked to take a next step. Reach out personally.</p>
      <div className="mt-6 grid md:grid-cols-2 gap-3">
        {items.length === 0 && <div className="md:col-span-2 bg-white border border-dashed border-[#e5dcc5] rounded-2xl p-8 text-center text-[#8a7a4e] text-sm">No requests yet.</div>}
        {items.map((n) => (
          <div key={n.id} data-testid={`admin-next-step-${n.id}`} className="bg-white border border-[#e5dcc5] rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-serif-display text-lg text-[#1f3a2a]">{n.name}</h3>
                <a href={`mailto:${n.email}`} className="text-sm text-[#2e5340] hover:underline">{n.email}</a>
                {n.phone && <span className="text-sm text-[#4a5a4e]"> · {n.phone}</span>}
              </div>
              <span className="text-xs text-[#8a7a4e] shrink-0">{new Date(n.created_at).toLocaleDateString()} · {(n.language || 'en').toUpperCase()}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {n.step_labels.map((s) => <span key={s} className="text-xs bg-[#f0e9d8] text-[#1f3a2a] rounded-full px-3 py-1">{s}</span>)}
            </div>
            {n.note && <p className="mt-3 text-sm text-[#4a5a4e] border-l-2 border-[#d4a63d] pl-3">{n.note}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

const fieldCls = 'mt-2 bg-[#faf6ea] border-[#e5dcc5] h-11 rounded-xl focus-visible:ring-[#2e5340]';

function SeriesFields({ meta, setMeta, items }) {
  const names = [...new Set(items.map((s) => s.series).filter(Boolean))];
  const onSeries = (series) => {
    const inSeries = items.filter((s) => s.series === series);
    if (!inSeries.length) return setMeta({ ...meta, series });
    const total = Math.max(...inSeries.map((s) => s.series_total || 0));
    setMeta({ series, session: String(Math.max(...inSeries.map((s) => s.session)) + 1), total: total ? String(total) : meta.total });
  };
  return (
    <div className="grid grid-cols-5 gap-3">
      <div className="col-span-3">
        <Label className="text-[#1f3a2a]">Series <span className="text-[#8a7a4e]">(optional)</span></Label>
        <Input list="series-options" data-testid="admin-study-series-input" value={meta.series} onChange={(e) => onSeries(e.target.value)} placeholder="Grow · Called Out & Gathered In" className={fieldCls} />
        <datalist id="series-options">{names.map((n) => <option key={n} value={n} />)}</datalist>
      </div>
      <div>
        <Label className="text-[#1f3a2a]">Session</Label>
        <Input type="number" min="1" data-testid="admin-study-session-input" disabled={!meta.series} value={meta.session} onChange={(e) => setMeta({ ...meta, session: e.target.value })} placeholder="1" className={fieldCls} />
      </div>
      <div>
        <Label className="text-[#1f3a2a]">of</Label>
        <Input type="number" min="1" data-testid="admin-study-total-input" disabled={!meta.series} value={meta.total} onChange={(e) => setMeta({ ...meta, total: e.target.value })} placeholder="4" className={fieldCls} />
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [meta, setMeta] = useState({ series: '', session: '', total: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = async () => {
    try { const d = await fetchStudies(); setItems(d.items || []); } catch (err) { console.error('Failed to load studies', err); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !file) {
      toast({ title: 'Missing info', description: 'Please add a title and choose a PDF.' });
      return;
    }
    if (meta.series.trim() && !(Number(meta.session) >= 1)) {
      toast({ title: 'Missing session number', description: 'Add which session number this study is in the series.' });
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      await uploadStudy(title.trim(), desc.trim(), file, setProgress, { ...meta, series: meta.series.trim() });
      toast({ title: 'Study uploaded', description: 'It is now live on the public site.' });
      setTitle(''); setDesc(''); setFile(null); setMeta({ series: '', session: '', total: '' });
      document.getElementById('study-file').value = '';
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed';
      toast({ title: 'Upload failed', description: detail });
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1200);
    }
  };

  const remove = async (id, titleText) => {
    if (!window.confirm(`Delete "${titleText}"? This can't be undone.`)) return;
    try {
      await deleteStudy(id);
      toast({ title: 'Deleted', description: `"${titleText}" has been removed.` });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-[#f5efe3]">
      <header className="bg-[#1f3a2a] text-[#f5efe3]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-[#2e5340] flex items-center justify-center text-[#d4a63d]"><Sprout size={20} /></span>
            <div>
              <p className="text-[10px] uppercase tracking-wider-xtra text-[#d4a63d]">Pastor Dashboard</p>
              <h1 className="font-serif-display text-xl">Planted Ministries</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline" className="rounded-full h-9 border-[#f5efe3]/30 bg-transparent text-[#f5efe3] hover:bg-[#f5efe3]/10">
              <a href="/" className="inline-flex items-center gap-2 text-sm"><ExternalLink size={14} /> View site</a>
            </Button>
            <Button onClick={onLogout} className="bg-[#d4a63d] hover:bg-[#b88a2a] text-[#1f3a2a] rounded-full h-9 px-4">
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <EventAdmin />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-5 gap-10">
        <section className="lg:col-span-2">
          <h2 className="font-serif-display text-2xl text-[#1f3a2a]">Upload a new Bible study</h2>
          <p className="text-sm text-[#4a5a4e] mt-1">PDF up to 25 MB. It appears on the public site immediately.</p>
          <form onSubmit={submit} className="mt-6 bg-white border border-[#e5dcc5] rounded-2xl p-6 space-y-4">
            <div>
              <Label className="text-[#1f3a2a]">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Psalm 1 — Rooted by Streams" className="mt-2 bg-[#faf6ea] border-[#e5dcc5] h-11 rounded-xl focus-visible:ring-[#2e5340]" />
            </div>
            <div>
              <Label className="text-[#1f3a2a]">Short description <span className="text-[#8a7a4e]">(optional)</span></Label>
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="A one-line summary that shows under the title" className="mt-2 bg-[#faf6ea] border-[#e5dcc5] rounded-xl focus-visible:ring-[#2e5340] min-h-[80px]" />
            </div>
            <SeriesFields meta={meta} setMeta={setMeta} items={items} />
            <div>
              <Label className="text-[#1f3a2a]">PDF file</Label>
              <input
                id="study-file"
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-2 block w-full text-sm text-[#4a5a4e] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-[#1f3a2a] file:text-[#f5efe3] hover:file:bg-[#2e5340]"
              />
              {file && <p className="mt-2 text-xs text-[#8a7a4e]">{file.name} · {fmtSize(file.size)}</p>}
            </div>
            {progress > 0 && <Progress value={progress} className="h-2 bg-[#f0e9d8]" />}
            <Button type="submit" disabled={uploading} className="w-full bg-[#d4a63d] hover:bg-[#b88a2a] text-[#1f3a2a] rounded-full h-11 font-medium">
              <Upload size={16} className="mr-2" /> {uploading ? 'Uploading…' : 'Publish study'}
            </Button>
          </form>
        </section>

        <section className="lg:col-span-3">
          <h2 className="font-serif-display text-2xl text-[#1f3a2a]">Published studies <span className="text-[#8a7a4e] text-base">({items.length})</span></h2>
          <p className="text-sm text-[#4a5a4e] mt-1">Download or remove any file. Deletions hide the file from the public site; the file stays in storage for safety.</p>
          <div className="mt-6 space-y-3">
            {items.length === 0 && (
              <div className="bg-white border border-dashed border-[#e5dcc5] rounded-2xl p-8 text-center text-[#8a7a4e] text-sm">
                No studies yet. Upload your first one to the left.
              </div>
            )}
            {items.map((s) => (
              <div key={s.id} className="bg-white border border-[#e5dcc5] rounded-2xl p-5 flex items-start gap-4">
                <span className="w-11 h-11 rounded-xl bg-[#f0e9d8] flex items-center justify-center text-[#2e5340] shrink-0"><FileText size={20} /></span>
                <div className="flex-1 min-w-0">
                  {s.series && <span className="text-[10px] uppercase tracking-wider-xtra text-[#b88a2a] font-medium">{s.series.split(' · ')[0]} · Session {s.session}{s.series_total ? ` of ${s.series_total}` : ''}</span>}
                  <h3 className="font-serif-display text-lg text-[#1f3a2a] leading-tight truncate">{s.title}</h3>
                  {s.description && <p className="text-sm text-[#4a5a4e] mt-1 line-clamp-2">{s.description}</p>}
                  <p className="text-xs text-[#8a7a4e] mt-1">PDF · {fmtSize(s.size)} · {new Date(s.uploaded_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button asChild variant="outline" size="sm" className="rounded-full border-[#1f3a2a]/20 text-[#1f3a2a] hover:bg-[#1f3a2a]/5">
                    <a href={studyDownloadUrl(s.id)} target="_blank" rel="noreferrer" aria-label="Open PDF"><Download size={14} /></a>
                  </Button>
                  <Button onClick={() => remove(s.id, s.title)} variant="outline" size="sm" className="rounded-full border-red-200 text-red-700 hover:bg-red-50">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <NextStepsAdmin />
    </div>
  );
}

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const existing = getAdminKey();
    if (!existing) { setChecking(false); return; }
    (async () => {
      try { await adminLogin(existing); setAuthed(true); } catch (_) { clearAdminKey(); }
      setChecking(false);
    })();
  }, []);

  if (checking) return <div className="min-h-screen bg-[#f5efe3] flex items-center justify-center text-[#4a5a4e]">Loading…</div>;

  return (
    <>
      {authed ? <Dashboard onLogout={() => { clearAdminKey(); setAuthed(false); }} /> : <LoginView onLogin={() => setAuthed(true)} />}
      <Toaster />
    </>
  );
}
